import {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {useLocation} from "react-router";
import axios from "axios";
import Spinner from "../../components/spinner";
import Input from "@autocx/forms/src/controls/input";
import getSafeReturnUrl from "./account-return-url";
import RegistrationForm, {RouteLink} from "./registration-form";
import AuthFormShell from "./auth-form-shell";
import {
    accountRegistrationRequiresApproval,
    getApprovalReturnUrl
} from "./registration-utils";

export {getSafeReturnUrl};
export {accountRegistrationRequiresApproval, getApprovalReturnUrl};

export default function AccountLoginRegister(props) {
    const location = useLocation();
    const from = getSafeReturnUrl(location);
    const isSystemPage = String(props.websiteContext?.node?.nodeType || '').toLowerCase() === 'systempage';

    useEffect(() => {
        if (!isSystemPage) document.title = props?.title
        if (props.profile?.customer?._links?.edit) {
            window.location.replace(`${window.location.origin}${from === "/" ? "/my-account/profile" : from}`)
        }
    }, [])

    const isLogin = location.pathname.includes("sign-in");
    const isResetPassword = location.pathname.includes("reset-password");
    const isSetPassword = location.pathname.includes("set-password");
    // Resolve the destination on the server even when this page used the
    // combined-account fallback: registration may have its own configured page.
    const accountLinkComponent = 'a';


    const heading = props.values?.heading || props?.title || 'Sign In';

    return (
        <AuthFormShell {...props} defaultWidth="narrow">
                <h2 className={"text-center !mb-4"}>{heading}</h2>
                {props.values?.introText ? (
                    <div
                        className="rich-text text-center mb-6"
                        dangerouslySetInnerHTML={{__html: props.values.introText}}
                    />
                ) : null}
                {isResetPassword ? <ResetPassword/>
                    : isLogin ? <LoginForm from={from} linkComponent={accountLinkComponent}/>
                        : isSetPassword ? <SetPassword reset={true}/>
                            : <RegisterForm from={from} linkComponent={accountLinkComponent} {...props}/>}
        </AuthFormShell>
    )
}

export const LoginForm = ({
    from,
    inline,
    setProfile,
    setShowingSignIn,
    setCurrentForm,
    linkComponent = 'a',
    preview = false
}) => {
    const [loading, setLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState("")
    const [formData, setFormData] = useState({
        emailAddress: '',
        password: ''
    });

    const handleChange = (e) => {
        setErrorMessage("")
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (preview) return;
        setLoading(true)
        axios.post('/api/v2/publicusers/authenticate', formData)
            .then(() => {
                if (inline) {
                    return axios.get('/api/v2/publicusers/profile/');
                } else {
                    location.replace(`${location.origin}${from}`);
                    return null;
                }
            })
            .then(profileResponse => {
                if (!profileResponse) return;
                setProfile(profileResponse.data);
                setShowingSignIn(false);
            })
            .catch(function (err) {
                if (err?.response?.status === 401) {
                    setErrorMessage('Your login details are incorrect')
                } else {
                    setErrorMessage('There was a problem logging in, please try again.');
                }
                console.log(err);
            })
            .finally(() => {
                setLoading(false)
            })
    };

    return (
        <div>
            <form className={"flex flex-col gap-y-3"} onSubmit={handleSubmit}>
                <input type="email" placeholder="Email" autoComplete="username" name="emailAddress"
                       value={formData.emailAddress} onChange={handleChange}/>
                <Input type="password" canRevealSecret={true} placeholder="Password" autoComplete="current-password"
                       id="current-password"
                       name="password" value={formData.password} onChange={handleChange}/>
                <button
                    type={preview ? "button" : "submit"}
                    disabled={loading}
                    aria-disabled={preview || undefined}
                    className={"button justify-center mt-2"}
                >
                    {loading ?
                        <>
                            <span className={"inline-block pr-4"}>Signing In...</span><Spinner
                            containerClassName={"!my-0"}/>
                        </>
                        : <>Sign In</>
                    }
                </button>
                {errorMessage && <div
                    className={"text-red-500 font-semibold leading-tight text-sm text-center animate__animated animate__headShake"}>{errorMessage}</div>}
            </form>
            {inline ?
                <>
                    <p className={"mt-2 text-center font-semibold text-sm"}>
                        <button className={"text-link"} onClick={() => setCurrentForm("resetPassword")}>Forgot
                            Password?
                        </button>
                    </p>
                    <p className={"mt-2 text-center text-base"}>
                        No account? You can easily register after checkout.
                    </p>
                </>
                :
                <>
                    <p className={"mt-2 text-center font-semibold text-sm"}>
                        <RouteLink
                            component={linkComponent}
                            href={`/my-account/reset-password${from && from !== "/" ? `?returnUrl=${encodeURIComponent(from)}` : ""}`}
                            aria-disabled={preview || undefined}
                            tabIndex={preview ? -1 : undefined}
                            onClick={preview ? event => event.preventDefault() : undefined}
                        >
                            Forgot Password?
                        </RouteLink>
                    </p>
                    <p className={"mt-4 text-center"}>
                        Don't have an account?{' '}
                        <RouteLink
                            component={linkComponent}
                            href={`/my-account/register${from && from !== "/" ? `?returnUrl=${encodeURIComponent(from)}` : ""}`}
                            aria-disabled={preview || undefined}
                            tabIndex={preview ? -1 : undefined}
                            onClick={preview ? event => event.preventDefault() : undefined}
                        >
                            Register
                        </RouteLink>
                    </p>
                </>
            }

        </div>
    );
};

export const RegisterForm = ({from, linkComponent = 'a', ...props}) => {
    return (
        <RegistrationForm
            {...props}
            from={from}
            linkComponent={linkComponent}
            node={props.node || {path: '/my-account/register'}}
        />
    );
};

export const ResetPassword = ({inline, setCurrentForm}) => {
    const [resetSent, setResetSent] = useState(false)
    const [loading, setLoading] = useState(false)
    const [email, setEmail] = useState('');

    const handleSubmit = (e) => {
        setLoading(true)
        e.preventDefault();
        axios.post('/api/v2/publicusers/password/request-reset', {email: email})
            .then(() => {
                setResetSent(true);
            })
            .finally(() => {
                setLoading(false)
            })
    };

    return (
        <div>
            {resetSent
                ?
                <p className={"leading-tight"}>
                    An email with password reset instructions will be sent if there's an account with the email <span
                    className={"font-semibold"}>{email}</span>.
                    <br/><br/>
                    Remember to check your spam folder too.
                </p>
                :
                <form className={"flex flex-col gap-y-3"} onSubmit={handleSubmit}>
                    <input type="email" placeholder="Email" autoComplete="username" required value={email}
                           onChange={(e) => setEmail(e.target.value)}
                    />
                    <button disabled={loading} type="submit" className={"button justify-center mt-2"}>
                        Reset Password
                    </button>
                </form>
            }
            {inline ?
                <p className={"mt-2 text-center"}>
                    <button className={"text-link"} onClick={() => setCurrentForm("signIn")}>Back to sign in</button>
                </p>
                :
                <p className={"mt-2 text-center"}>
                    <a href={`/my-account/sign-in${window.location.search || ""}`}>Back to sign in</a>
                </p>
            }

        </div>
    );
};

export const SetPassword = ({email, profile, ...props}) => {
    const [loading, setLoading] = useState(false)
    const [state, setState] = useState("guest")
    const [errorMessage, setErrorMessage] = useState("")
    const [formData, setFormData] = useState({
        password: ''
    });

    const handleChange = (e) => {
        setErrorMessage("")
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    useEffect(() => {
        if (profile) {
            if (profile?.registered) setState('registered')
            else if (profile?.customer?._links?.setPassword) setState('guest')
            else if (profile?.customer?._links?.login) setState('existing')
            else setState('unavailable')
        }
    }, [profile]);


    const url = props.reset ? '/api/v2/publicusers/password/reset' + window.location.search : '/api/v2/publicusers/password';

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true)
        axios.post(url, formData)
            .then(() => {
                if (props.onSuccess) props.onSuccess();
                props.reset ? location.replace(`${location.origin}/my-account/`) : setState('registered')
            })

            .catch(function (err) {
                props.reset ? setErrorMessage(<div>Looks like your reset password link has expired. <Link to="/my-account/reset-password">Please try again</Link></div>) : setErrorMessage('There was a problem setting your password, please try again.');
                console.log(err);
            })
            .finally(() => {
                setLoading(false)
            })
    };

    return (
        <div>
            {state === 'guest' &&
                <form className={"flex flex-col gap-y-3"} onSubmit={handleSubmit}>
                    <input readOnly type="email" placeholder="Email" autoComplete="username emailAddress"
                           name="emailAddress" value={email} className={"opacity-0 absolute pointer-events-none"}/>
                    <Input canRevealSecret={true} type="password" disabled={loading} placeholder="Password"
                           autoComplete="new-password" required={props.reset}
                           id="new-password" name="password" value={formData.password} onChange={handleChange}/>
                    <button type="submit" disabled={loading} className={"button justify-center mt-2"}>
                        Set Password
                    </button>
                    {errorMessage && <div
                        className={"text-red-500 font-semibold leading-tight text-sm text-center animate__animated animate__headShake"}>{errorMessage}</div>}
                </form>
            }
            {state === 'registered' && <div className={"leading-tight text-center"}>Thank you, your password has
                been set.</div>}
            {state === 'existing' &&
                <div className={"leading-tight text-center"}>Looks like you have an account
                    with us, <a href='/my-account/sign-in'>log in here </a> to view all your
                    orders.</div>}
        </div>
    );
};
